import React, { useState, useEffect } from "react";
import { Auth } from "../../../Common/lib/auth";
import { ImSpinner8 } from "react-icons/im";
import {formatPhoneNumber, unformatPhoneNumber} from "../../../Common/formatInput";

export default function EmailNameStep({ userInfo, setUserInfo, currentStep, setCurrentStep, classNames }) {
  const [errors, setErrors] = useState({
    emailError: "",
    firstNameError: "",
    lastNameError: "",
    phoneNumberError: "",
  });
  const [loading, setLoading] = useState(false);

  const requiredFields = ["email", "firstName", "lastName"];

  // Handles clearing errors when a user starts editing a field.
  useEffect(() => {
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      if (userInfo[field].trim().length > 0) {
        setErrors(prevState => ({
          ...prevState,
          [`${field}Error`]: "",
        }));
      }
    }
  }, [userInfo]);

  /**
   * Moves user to the next step of the sign up process. Validates before moving to the next step.
   */
  async function nextStep() {
    let isValid = await validateStep();
    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  /**
   * Validates the current step of the sign up page.
   * @returns {boolean} Validation result. True if form is valid, false if form is invalid.
   */
  async function validateStep() {
    let isValid = true;
    setLoading(true);

    // Check if any of the fields are empty ---------------
    if (!requiredFields.every(field => userInfo[field].trim() !== "")) {
      for (let i = 0; i < requiredFields.length; i++) {
        const field = requiredFields[i];
        if (userInfo[field] === "") {
          setErrors(prevState => ({
            ...prevState,
            [`${field}Error`]: "Required",
          }));
          isValid = false;
        }
      }
      setLoading(false);
      return false;
    }

    // Check if email provided is valid -------------------
    if (!validateEmail(userInfo.email)) {
      setErrors((prevState) => ({
        ...prevState,
        emailError: "Invalid email",
      }));
      isValid = false;
    }

    let checkExists = await checkEmailExists(); // Check if an account already exists with this email
    // let checkDeliverable = await checkEmailDeliverable(userInfo.email); // Check if email is deliverable to prevent hard bounces

    // Check if email provided is deliverable
    /* if (!checkDeliverable) {
      setErrors((prevState) => ({
        ...prevState,
        emailError: "Invalid email",
      }));
      isValid = false;
    } */

    // Check if email provided already exists
    if (checkExists) {
      setErrors((prevState) => ({
        ...prevState,
        emailError: "An account with this email already exists",
      }));
      isValid = false;
    }

    // Check if first name is valid -----------------------
    if (userInfo.firstName.length > 50) {
      setErrors(prevState => ({
        ...prevState,
        firstNameError: "Cannot be longer than 50 characters",
      }));
      isValid = false;
    } else if (!validateName(userInfo.firstName)) {
      setErrors(prevState => ({
        ...prevState,
        firstNameError: "Cannot contain special characters",
      }));
      isValid = false;
    }

    // Check if last name is valid ------------------------
    if (userInfo.lastName.length > 50) {
      setErrors(prevState => ({
        ...prevState,
        lastNameError: "Cannot be longer than 50 characters",
      }));
      isValid = false;
    } else if (!validateName(userInfo.lastName)) {
      setErrors(prevState => ({
        ...prevState,
        lastNameError: "Cannot contain special characters",
      }));
      isValid = false;
    }

      // Check if phone number is valid
    if (!validatePhoneNumber(userInfo.phoneNumber)) {
      setErrors(prevState => ({
        ...prevState,
        phoneNumberError: "Invalid phone number",
      }));
      isValid = false;
    }

    setLoading(false);
    return isValid;
  }

  /**
  * Email format validation.
  * @param email User email to validate
  * @returns {boolean} Validation result. True if email is valid, false if email is invalid.
  */
  function validateEmail(email) {
    const EMAIL_REGEXP = RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,5})+$/)
    return EMAIL_REGEXP.test(email);
  };

  /**
   * Checks if the user entered email is already in use.
   * @returns {boolean} Validation result. Returns true if an account with that email already exists, and returns false if no account with that email is found.
   */
  async function checkEmailExists() {
    try {
      // Attempt sign in with wrong credentials (cognito doesn't have a function for checking existing users)
      await Auth.signIn(userInfo.email, "123");
    }
    catch (error) {
      switch (error.code) {
        case 'UserNotConfirmedException':
          return true;
        case 'NotAuthorizedException':
          return true;
        case 'PasswordResetRequiredException':
          return true;
        default:
          return false;
      }
    }
  }

  /**
   * Checks if the provided user email is deliverable, not disposable, and not spam. Helps with preventing email hard bounces.
   * @param {*} email User email to check.
   * @returns boolean - result of API email validation. True if email is valid, false if email is invalid.
   */
  async function checkEmailDeliverable(email) {
    let deliverable = false;
    // https://www.abstractapi.com/api/email-verification-validation-api
    return fetch(`https://emailvalidation.abstractapi.com/v1/?api_key=API_KEY&email=` + email)
      .then(response => {
        return response.json();
      })
      .then(responseData => {
        deliverable = (responseData.deliverability !== "UNDELIVERABLE" && !responseData.is_disposable_email.value);
        return deliverable;
      }).catch(error => {
        return false;
      })
  }

  /**
   * Validates name to ensure no special characters aside from ' - are present.
   * @param {*} name 
   * @returns {boolean} Validation result. True if name is valid, false if name is invalid.
   */
  function validateName(name) {
    const nameRegex = RegExp(/^[a-zA-Z' -]+$/);
    return nameRegex.test(name);
  }

  /**
 * Validates phone number to ensure it is 10 digits long.
 * @param {*} phone 
 * @returns {boolean} Validation result. True if phone number is valid, false if phone number is invalid.
 */
function validatePhoneNumber(phone) {
  const PHONE_REGEXP = RegExp(/^\d{10}$/);
  return PHONE_REGEXP.test(phone);
}


  /**
   * Handles input field changes.
   * @param {*} field Name of userInfo property to change
   * @param {*} value Value from input field
   */
  function handleChange(field, value) {
    if (field === "phoneNumber") {
      setUserInfo((prevState) => ({
        ...prevState,
        phoneNumber: unformatPhoneNumber(value),
        tempPhoneNumber: formatPhoneNumber(value),
      }));
    } else {
      setUserInfo((prevState) => ({
        ...prevState,
        [field]: typeof value === "string" ? value.trim() : value,
      }));
    }
  }


  

  return (
    <>
      <div className="mb-6 sm:col-span-2">
        <label htmlFor="email" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Email
          {errors.emailError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.emailError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("email", e.target.value)}
            defaultValue={userInfo.email}
            type="email"
            name="email"
            id="email"
            className={classNames(errors.emailError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>
      <div className="mb-6 sm:col-span-2">
      <label htmlFor="phoneNumber" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
        Phone Number
        {errors.phoneNumberError.length > 0 &&
          <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
            &nbsp;- {errors.phoneNumberError}
          </p>
        }
      </label>
      <div className="mt-1">
        <input
          value={userInfo.tempPhoneNumber}
          onChange={(e) => handleChange("phoneNumber", e.target.value)}
          type="text"
          name="phoneNumber"
          id="phoneNumber"
          className={classNames(errors.phoneNumberError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
        />
      </div>
      </div>
      <div className="mb-6 sm:col-span-2">
        <label htmlFor="firstName" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          First Name
          {errors.firstNameError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.firstNameError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("firstName", e.target.value)}
            defaultValue={userInfo.firstName}
            type="text"
            name="firstName"
            id="firstName"
            className={classNames(errors.firstNameError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>

      <div className="mb-6 sm:col-span-2">
        <label htmlFor="lastName" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Last Name
          {errors.lastNameError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600 dark:text-red-500">
              &nbsp;- {errors.lastNameError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            onChange={(e) => handleChange("lastName", e.target.value)}
            defaultValue={userInfo.lastName}
            type="text"
            name="lastName"
            id="lastName"
            className={classNames(errors.lastNameError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                nextStep()
              }
            }}
          />
        </div>
      </div>

      <div className="text-sm sm:text-base gap-14 sm:gap-24 flex justify-between">
        <button
          onClick={() => nextStep()}
          data-cy="next"
          type="button"
          className="inline-flex items-center justify-center gap-x-2 rounded-md w-full bg-blue-600 py-2.5 px-3.5 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Next
          {loading &&
            <ImSpinner8 className="w-5 h-5 text-white animate-spin" />
          }
        </button>
      </div>
    </>
  )
}

/* ඞ */